import React, { useState, useEffect, useContext } from "react";
import { useLoading } from "../../../shared/context/LoadingContext";
import "./ModalStockCadastrar.scss";
import uuid from "react-uuid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import moment from "moment";

import { IconButton } from "@mui/material";
import useLocalizacoes from "./useLocalizacoes";
import NotaInformativa from "../../../shared/components/notaInformativa/NotaInformativa";

const UNIDADES_MEDIDA = [
  { value: "UN", label: "Unidade" },
  { value: "KG", label: "Quilograma (kg)" },
  { value: "M", label: "Metro (m)" },
  { value: "L", label: "Litro (l)" },
  { value: "M3", label: "Metro Cúbico (m³)" },
  { value: "M2", label: "Metro Quadrado (m²)" },
  { value: "CX", label: "Caixa" },
];

// ─── Select encadeado Armazém → Localização ──────────────────────────────────
function LocSelect({ produto, armazens, localizacoesPorArmazem, onChange }) {
  const locs = localizacoesPorArmazem(produto.armazem);

  const handleArmazem = (armazemId) => {
    const primeiraLoc = localizacoesPorArmazem(armazemId)[0];
    onChange(produto.id, "armazem", armazemId);
    onChange(produto.id, "localizacao", primeiraLoc?.id || "");
  };

  return (
    <div style={{ display: "flex", gap: 4, flexDirection: "column" }}>
      {/* Armazém */}
      <TextField
        select
        value={produto.armazem || ""}
        onChange={(e) => handleArmazem(e.target.value)}
        variant="standard"
        fullWidth
        size="small"
        label="Armazém"
        error={!produto.armazem}
        helperText={!produto.armazem ? "Obrigatório" : ""}
      >
        {armazens.length === 0 && (
          <MenuItem value="" disabled>
            Nenhum armazém criado
          </MenuItem>
        )}
        {armazens.map((a) => (
          <MenuItem key={a.id} value={a.id}>
            {a.codigo} — {a.nome}
          </MenuItem>
        ))}
      </TextField>

      {/* Localização */}
      <TextField
        select
        value={produto.localizacao || ""}
        onChange={(e) => onChange(produto.id, "localizacao", e.target.value)}
        variant="standard"
        fullWidth
        size="small"
        label="Localização"
        disabled={!produto.armazem || locs.length === 0}
        error={produto.armazem && !produto.localizacao}
        helperText={
          !produto.armazem
            ? ""
            : locs.length === 0
              ? "Sem localizações"
              : !produto.localizacao
                ? "Obrigatório"
                : ""
        }
      >
        {locs.map((l) => (
          <MenuItem key={l.id} value={l.id}>
            {l.codigo} — {l.nome}
          </MenuItem>
        ))}
      </TextField>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
function ModalStockCadastrar({
  handleSubmitEntradaStock,
  handleCloseModalAdicionarStock,
  categoriasStock,
  sendRequest,
  clinicaId,
  auth,
}) {
  const { startLoading, stopLoading } = useLoading();

  const {
    armazens,
    localizacoesPorArmazem,
    primeiraLocalizacao,
    loading: loadingLocs,
  } = useLocalizacoes({ sendRequest, clinicaId, auth });

  // Default inicial: primeiro armazém e primeira localização
  const defaultArmazem = armazens[0]?.id || "";
  const defaultLocalizacao =
    (armazens[0]?.id ? primeiraLocalizacao(armazens[0].id)?.id : "") || "";

  const [dadosProdutos, setDadosProdutos] = useState([
    {
      id: 0,
      nome: "",
      categoriaStock: "",
      quantidade: 1,
      quantidadeIdeal: 1,
      lote: "N/A",
      validade: null,
      unidadeMedida: "UN",
      classificacao: "PA",
      fornecedor: "",
      precoCompra: 0,
      armazem: "",
      localizacao: "",
    },
  ]);
  const [fixarCategoria, setFixarCategoria] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [usarLotesAutomaticos, setUsarLotesAutomaticos] = useState(true);
  const [fornecedores, setFornecedores] = useState([]);
  const [categoriesTree, setCategoriesTree] = useState([]);

  // Actualizar defaults quando os armazéns carregam
  useEffect(() => {
    if (armazens.length > 0) {
      const arm = armazens[0].id;
      const loc = primeiraLocalizacao(arm)?.id || "";
      setDadosProdutos((prev) =>
        prev.map((p) =>
          p.armazem === "" ? { ...p, armazem: arm, localizacao: loc } : p,
        ),
      );
    }
  }, [armazens]);

  const buildCategoryTree = (categories, parentId = null) => {
    if (!categories || !Array.isArray(categories)) return [];
    return categories
      .filter((c) =>
        parentId === null
          ? c.categoriaPai === null || c.categoriaPai === undefined
          : c.categoriaPai === parentId,
      )
      .map((c) => ({
        ...c,
        subcategorias: buildCategoryTree(categories, c.id),
      }));
  };

  useEffect(() => {
    if (categoriasStock) setCategoriesTree(buildCategoryTree(categoriasStock));
  }, [categoriasStock]);

  useEffect(() => {
    const fetchFornecedores = async () => {
      startLoading();
      try {
        const res = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/fornecedores/clinica/stock/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        if (res?.fornecedores) setFornecedores(res.fornecedores);
      } catch (err) {
        console.error(err);
      } finally {
        stopLoading();
      }
    };
    fetchFornecedores();
  }, [sendRequest, clinicaId, auth.token]);

  const handleInputChange = (id, field, value) => {
    setDadosProdutos((curr) =>
      curr.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
    );
  };

  const handleDelete = (id) => {
    setDadosProdutos((curr) => curr.filter((p) => p.id !== id));
  };

  const handleAdicionar = () => {
    setDadosProdutos((curr) => {
      const last = curr[curr.length - 1];
      const arm = fixarCategoria ? last.armazem : armazens[0]?.id || "";
      const loc = fixarCategoria
        ? last.localizacao
        : primeiraLocalizacao(arm)?.id || "";

      return [
        ...curr,
        {
          id: uuid(),
          nome: "",
          categoriaStock: fixarCategoria ? last.categoriaStock : "",
          quantidade: 1,
          quantidadeIdeal: 1,
          lote: usarLotesAutomaticos ? "" : "N/A",
          validade: null,
          unidadeMedida: fixarCategoria ? last.unidadeMedida : "UN",
          classificacao: fixarCategoria ? last.classificacao : "PA",
          fornecedor: fixarCategoria ? last.fornecedor : "",
          precoCompra: 0,
          armazem: arm,
          localizacao: loc,
        },
      ];
    });
  };

  const preHandleSubmitEntradaStock = () => {
    let erro = "";
    dadosProdutos.forEach((d) => {
      if (
        !d.nome ||
        d.nome?.trim === "" ||
        d.quantidade === undefined ||
        d.quantidade === null ||
        d.quantidade === "" ||
        !d.categoriaStock ||
        d.categoriaStock?.trim() === ""
      ) {
        erro =
          "Por favor, preencha todos os campos obrigatórios para cada produto.";
        return;
      }
      if (!d.armazem) {
        erro = "Por favor, selecione o armazém para cada produto.";
        return;
      }
      if (!d.localizacao) {
        erro = "Por favor, selecione a localização para cada produto.";
        return;
      }
      if (d.validade) {
        if (!moment.isMoment(d.validade) || !d.validade.isValid()) {
          erro = "Introduza a data de validade corretamente";
          return;
        }
      }
    });

    if (erro) {
      setValidationError(erro);
    } else {
      setValidationError("");
      handleSubmitEntradaStock(dadosProdutos);
    }
  };

  const renderCategoryOptions = (categories, level = 0) => {
    if (!categories) return null;
    return categories.flatMap((c) => [
      <MenuItem
        key={c.id}
        value={c.id}
        style={{
          paddingLeft: `${level * 20}px`,
          fontWeight: level === 0 ? "bold" : "normal",
        }}
      >
        {level > 0 ? `${"\u00A0".repeat(level * 2)}└─ ${c.nome}` : c.nome}
      </MenuItem>,
      ...(c.subcategorias
        ? renderCategoryOptions(c.subcategorias, level + 1)
        : []),
    ]);
  };

  return (
    <div className="stock-cadastrar-container">
      <div className="stock-cadastrar-header">
        <h3>Cadastro de Produtos no Stock</h3>
        <p>
          Preencha os dados dos produtos a serem adicionados ao stock.
          <Tooltip title="Os campos Nome, Categoria, Quantidade, Armazém e Localização são obrigatórios.">
            <InfoIcon className="stock-cadastrar-info-icon" />
          </Tooltip>
        </p>

        {/* O erro caro aqui é cadastrar de novo um produto que já existe: passa
            a haver duas fichas com o mesmo nome, o total fica repartido entre
            as duas e os alertas de reposição deixam de bater certo. Por isso é
            o primeiro ponto. */}
        <NotaInformativa
          titulo="Esta janela cria produtos novos"
          style={{ margin: "12px 0 4px" }}
        >
          <ul style={{ margin: 0, paddingLeft: "18px" }}>
            <li>
              Use isto só para um produto que <strong>ainda não existe</strong>{" "}
              no stock. Se já existe, volte atrás e escolha dar entrada no
              produto existente — senão fica com duas fichas do mesmo produto e
              a quantidade repartida entre as duas.
            </li>
            <li>
              O <strong>nome</strong> é o que vai ver em todo o lado. Escreva-o
              como quer encontrá-lo na lista, com marca e medida se isso
              distinguir o produto de outro parecido.
            </li>
            <li>
              A <strong>quantidade ideal</strong> é o nível a partir do qual o
              sistema avisa que é preciso repor.
            </li>
            <li>
              <strong>Armazém e localização</strong> são obrigatórios: é onde as
              unidades ficam guardadas e por onde depois se procura.
            </li>
          </ul>
        </NotaInformativa>

        {loadingLocs && (
          <p style={{ fontSize: 12, color: "#1976d2" }}>
            A carregar armazéns e localizações...
          </p>
        )}

        {!loadingLocs && armazens.length === 0 && (
          <p style={{ fontSize: 12, color: "#c62828", fontWeight: 500 }}>
            ⚠ Nenhum armazém encontrado. Crie armazéns e localizações na gestão
            de armazéns antes de adicionar produtos.
          </p>
        )}

        <div className="stock-cadastrar-options">
          <FormControlLabel
            control={
              <Checkbox
                checked={fixarCategoria}
                onChange={(e) => setFixarCategoria(e.target.checked)}
                color="primary"
              />
            }
            label="Fixar categoria, unidade e localização ao adicionar novos produtos"
            className="stock-cadastrar-option"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={usarLotesAutomaticos}
                onChange={(e) => setUsarLotesAutomaticos(e.target.checked)}
                color="primary"
              />
            }
            label="Gerar lotes automáticos para itens sem lote especificado"
            className="stock-cadastrar-option"
          />
        </div>
      </div>

      <div className="stock-cadastrar-table-container">
        <table className="stock-cadastrar-table">
          <thead>
            <tr>
              <th>Nome do Produto*</th>
              <th>Quantidade*</th>
              <th>Stock Min</th>
              <th>Lote</th>
              <th>Validade</th>
              <th>Un. Medida</th>
              <th>Categoria*</th>
              <th>Classificação</th>
              <th>Preço Unit.</th>
              <th>Fornecedor</th>
              <th>Armazém / Localização*</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {dadosProdutos.map((produto, index) => (
              <tr key={produto.id} className="stock-cadastrar-row">
                {/* Nome */}
                <td>
                  <TextField
                    value={produto.nome}
                    onChange={(e) =>
                      handleInputChange(produto.id, "nome", e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    error={!produto.nome}
                  />
                </td>

                {/* Quantidade */}
                <td>
                  <TextField
                    type="number"
                    value={produto.quantidade}
                    onChange={(e) =>
                      handleInputChange(
                        produto.id,
                        "quantidade",
                        e.target.value,
                      )
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                    error={
                      produto.quantidade === "" ||
                      produto.quantidade === undefined ||
                      produto.quantidade === null
                    }
                  />
                </td>

                {/* Stock Min */}
                <td>
                  <TextField
                    type="number"
                    value={produto.quantidadeIdeal}
                    onChange={(e) =>
                      handleInputChange(
                        produto.id,
                        "quantidadeIdeal",
                        e.target.value,
                      )
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 1, step: 0.01 } }}
                  />
                </td>

                {/* Lote */}
                <td>
                  <TextField
                    value={produto.lote}
                    onChange={(e) =>
                      handleInputChange(produto.id, "lote", e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    placeholder={usarLotesAutomaticos ? "Auto-gerado" : "N/A"}
                    disabled={usarLotesAutomaticos}
                  />
                </td>

                {/* Validade */}
                <td>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DesktopDatePicker
                      inputFormat="DD/MM/YYYY"
                      value={produto.validade}
                      onChange={(date) =>
                        handleInputChange(produto.id, "validade", date)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </LocalizationProvider>
                </td>

                {/* Unidade */}
                <td>
                  <TextField
                    select
                    value={produto.unidadeMedida}
                    onChange={(e) =>
                      handleInputChange(
                        produto.id,
                        "unidadeMedida",
                        e.target.value,
                      )
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                  >
                    {UNIDADES_MEDIDA.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </td>

                {/* Categoria */}
                <td>
                  <TextField
                    select
                    value={produto.categoriaStock}
                    onChange={(e) =>
                      handleInputChange(
                        produto.id,
                        "categoriaStock",
                        e.target.value,
                      )
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    error={!produto.categoriaStock}
                  >
                    {renderCategoryOptions(categoriesTree)}
                  </TextField>
                </td>

                {/* Classificação */}
                <td>
                  <TextField
                    select
                    value={produto.classificacao}
                    onChange={(e) =>
                      handleInputChange(
                        produto.id,
                        "classificacao",
                        e.target.value,
                      )
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="PA">Produto Acabado</MenuItem>
                    <MenuItem value="MP">Matéria Prima</MenuItem>
                  </TextField>
                </td>

                {/* Preço */}
                <td>
                  <TextField
                    type="number"
                    value={produto.precoCompra || ""}
                    onChange={(e) =>
                      handleInputChange(
                        produto.id,
                        "precoCompra",
                        e.target.value,
                      )
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                  />
                </td>

                {/* Fornecedor */}
                <td>
                  <TextField
                    select
                    value={produto.fornecedor || ""}
                    onChange={(e) =>
                      handleInputChange(
                        produto.id,
                        "fornecedor",
                        e.target.value,
                      )
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {fornecedores.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.nome}
                      </MenuItem>
                    ))}
                  </TextField>
                </td>

                {/* Armazém / Localização */}
                <td style={{ minWidth: 160 }}>
                  <LocSelect
                    produto={produto}
                    armazens={armazens}
                    localizacoesPorArmazem={localizacoesPorArmazem}
                    onChange={handleInputChange}
                  />
                </td>

                {/* Ações */}
                <td>
                  {index > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(produto.id)}
                      className="stock-cadastrar-delete-btn"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="stock-cadastrar-actions">
        <button
          type="button"
          className="stock-cadastrar-add-button"
          onClick={handleAdicionar}
        >
          <AddCircleOutlineIcon />
          Adicionar Produto
        </button>
      </div>

      {validationError && (
        <div className="stock-cadastrar-error">
          <span>{validationError}</span>
        </div>
      )}

      <div className="stock-cadastrar-footer">
        <button
          type="button"
          className="stock-cadastrar-cancel-btn"
          onClick={handleCloseModalAdicionarStock}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="stock-cadastrar-submit-btn"
          onClick={preHandleSubmitEntradaStock}
          disabled={armazens.length === 0}
        >
          Adicionar ao Stock
        </button>
      </div>
    </div>
  );
}

export default ModalStockCadastrar;
